import { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ChevronLeft, Ship, Trash2, Edit2, AlertTriangle, CheckCircle2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { useCruiseMetadata } from '../context/CruiseMetadataContext';
import { renameShipGlobally, deleteShipItinerariesGlobally } from '../services/adminService';

const AdminShipItineraryManager = () => {
  const { cruiseLines, refreshCruiseMetadata } = useCruiseMetadata();
  const [activeTab, setActiveTab] = useState<'rename' | 'delete'>('rename');

  // Form State
  const [selectedCruiseLine, setSelectedCruiseLine] = useState('');
  const [selectedShip, setSelectedShip] = useState('');
  const [newShipName, setNewShipName] = useState('');
  const [renameUserCruises, setRenameUserCruises] = useState(true);
  const [removeShipMetadata, setRemoveShipMetadata] = useState(true);

  // Status State
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<any | null>(null);

  // Get ships list for selected cruise line
  const selectedClData = cruiseLines.find(cl => cl.name === selectedCruiseLine);
  const ships = selectedClData ? [...(selectedClData.ships || []), ...(selectedClData.retiredShips || [])].sort() : [];

  const handleRename = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedCruiseLine || !selectedShip || !newShipName.trim()) {
      toast.error('Please fill in all fields');
      return;
    }

    if (selectedShip === newShipName.trim()) {
      toast.error('New ship name must be different from current name');
      return;
    }

    const confirmMsg = `Are you absolutely sure you want to rename "${selectedShip}" to "${newShipName.trim()}" globally?\n\nThis will update official itineraries, summaries, metadata lists, and optionally user cruises. This operation cannot be undone.`;
    if (!window.confirm(confirmMsg)) return;

    setLoading(true);
    setResult(null);
    try {
      const res = await renameShipGlobally(
        selectedCruiseLine,
        selectedShip,
        newShipName.trim(),
        renameUserCruises
      );
      setResult({ type: 'rename', ...res });
      toast.success('Ship renamed successfully!');
      await refreshCruiseMetadata();
      
      // Reset ship selection and input
      setSelectedShip('');
      setNewShipName('');
    } catch (error: any) {
      console.error('Error renaming ship:', error);
      toast.error(`Rename failed: ${error?.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedCruiseLine || !selectedShip) {
      toast.error('Please select a cruise line and ship');
      return;
    }

    const confirmMsg = `Are you absolutely sure you want to DELETE all official itineraries for "${selectedShip}"? \n\nThis deletes the sailing schedule. User cruises will NOT be deleted. This operation cannot be undone.`;
    if (!window.confirm(confirmMsg)) return;

    setLoading(true);
    setResult(null);
    try {
      const res = await deleteShipItinerariesGlobally(
        selectedCruiseLine,
        selectedShip,
        removeShipMetadata
      );
      setResult({ type: 'delete', ...res });
      toast.success('Itineraries deleted successfully!');
      await refreshCruiseMetadata();
      
      // Reset ship selection
      setSelectedShip('');
    } catch (error: any) {
      console.error('Error deleting itineraries:', error);
      toast.error(`Deletion failed: ${error?.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="max-w-6xl mx-auto space-y-6 pb-20"
    >
      <div className="flex items-center gap-4">
        <Link
          to="/admin"
          className="mr-4 p-2 rounded-full hover:bg-gray-200 transition-colors"
          aria-label="Go to Admin"
        >
          <ChevronLeft className="h-5 w-5 text-gray-600" />
        </Link>
        <div>
          <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-2">
            <Ship className="h-8 w-8 text-blue-600" />
            Ship & Itinerary Manager
          </h1>
          <p className="text-gray-600 mt-1">Mass rename ships globally or delete sailing schedules</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200 bg-white rounded-t-lg overflow-hidden shadow-sm">
        <button
          onClick={() => {
            setActiveTab('rename');
            setResult(null);
          }}
          className={`flex-1 py-4 text-center font-medium text-sm flex justify-center items-center gap-2 border-b-2 transition-colors ${
            activeTab === 'rename'
              ? 'border-blue-600 text-blue-600 bg-blue-50/30'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
          }`}
        >
          <Edit2 className="h-4 w-4" />
          Global Ship Rename
        </button>
        <button
          onClick={() => {
            setActiveTab('delete');
            setResult(null);
          }}
          className={`flex-1 py-4 text-center font-medium text-sm flex justify-center items-center gap-2 border-b-2 transition-colors ${
            activeTab === 'delete'
              ? 'border-red-600 text-red-600 bg-red-50/30'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
          }`}
        >
          <Trash2 className="h-4 w-4" />
          Delete Itineraries
        </button>
      </div>

      <div className="bg-white rounded-b-lg border border-t-0 border-gray-200 p-6 shadow-sm">
        <form onSubmit={activeTab === 'rename' ? handleRename : handleDelete} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Cruise Line Selection */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Cruise Line</label>
              <select
                value={selectedCruiseLine}
                onChange={(e) => {
                  setSelectedCruiseLine(e.target.value);
                  setSelectedShip('');
                }}
                required
                className="w-full rounded-md border border-gray-300 px-3 py-2 bg-white focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              >
                <option value="">-- Select Cruise Line --</option>
                {cruiseLines.map((cl) => (
                  <option key={cl.id} value={cl.name}>
                    {cl.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Ship Selection */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Ship</label>
              <select
                value={selectedShip}
                onChange={(e) => setSelectedShip(e.target.value)}
                required
                disabled={!selectedCruiseLine}
                className="w-full rounded-md border border-gray-300 px-3 py-2 bg-white focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 disabled:bg-gray-50 disabled:text-gray-400"
              >
                <option value="">
                  {!selectedCruiseLine ? 'Choose Cruise Line first' : '-- Select Ship --'}
                </option>
                {ships.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {activeTab === 'rename' ? (
            <div className="space-y-6">
              {/* New Ship Name Input */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">New Ship Name</label>
                <div className="relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Ship className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    value={newShipName}
                    onChange={(e) => setNewShipName(e.target.value)}
                    required
                    placeholder="Enter new ship name"
                    className="w-full rounded-md border border-gray-300 pl-10 pr-3 py-2 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  />
                </div>
              </div>

              {/* Rename User Cruises Option */}
              <div className="relative flex items-start">
                <div className="flex items-center h-5">
                  <input
                    id="rename-user-cruises"
                    type="checkbox"
                    checked={renameUserCruises}
                    onChange={(e) => setRenameUserCruises(e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                </div>
                <div className="ml-3 text-sm">
                  <label htmlFor="rename-user-cruises" className="font-medium text-gray-700">
                    Rename User Cruises
                  </label>
                  <p className="text-gray-500">
                    Also search and rename the ship name in all users' personal cruise logs. Highly recommended to keep user data linked.
                  </p>
                </div>
              </div>

              {/* Warning box */}
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-amber-800 text-sm flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
                <div>
                  <span className="font-semibold">Important Note:</span> Renaming the ship recreates all official itineraries under a new document ID and modifies key lists in <code>cruiseMetadata</code>. It will perform a cascade of write operations safely using Firestore batches.
                </div>
              </div>

              <button
                type="submit"
                disabled={loading || !selectedShip || !newShipName.trim()}
                className="w-full flex justify-center items-center gap-2 rounded-md bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 shadow-md transition-colors disabled:opacity-50"
              >
                {loading ? 'Processing Global Rename...' : 'Rename Ship Globally'}
              </button>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Remove Ship Metadata Option */}
              <div className="relative flex items-start">
                <div className="flex items-center h-5">
                  <input
                    id="remove-ship-metadata"
                    type="checkbox"
                    checked={removeShipMetadata}
                    onChange={(e) => setRemoveShipMetadata(e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                </div>
                <div className="ml-3 text-sm">
                  <label htmlFor="remove-ship-metadata" className="font-medium text-gray-700">
                    Remove Ship from Metadata
                  </label>
                  <p className="text-gray-500">
                    Remove this ship entirely from the active ship lists, descriptions, and images in <code>cruiseMetadata</code>.
                  </p>
                </div>
              </div>

              {/* Warning box */}
              <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-800 text-sm flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                <div>
                  <span className="font-semibold">Dangerous Operation:</span> This deletes all official itineraries and summaries associated with this ship. This will NOT delete user-logged cruises, but they will become "Unlinked" unless re-linked in the Data Integrity scanner.
                </div>
              </div>

              <button
                type="submit"
                disabled={loading || !selectedShip}
                className="w-full flex justify-center items-center gap-2 rounded-md bg-red-600 hover:bg-red-700 text-white font-semibold py-3 shadow-md transition-colors disabled:opacity-50"
              >
                {loading ? 'Deleting Itineraries...' : 'Delete Ship Itineraries'}
              </button>
            </div>
          )}
        </form>

        {/* Results Panel */}
        {result && (
          <div className="mt-8 border-t border-gray-200 pt-6">
            <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
              <CheckCircle2 className="h-6 w-6 text-emerald-500" />
              Operation Summary
            </h3>
            
            {result.type === 'rename' ? (
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                <div className="p-3 bg-white rounded shadow-sm border border-gray-100">
                  <p className="text-xs text-gray-500 font-semibold uppercase">Official Itineraries</p>
                  <p className="text-2xl font-bold text-gray-800 mt-1">{result.itinerariesRenamed}</p>
                </div>
                <div className="p-3 bg-white rounded shadow-sm border border-gray-100">
                  <p className="text-xs text-gray-500 font-semibold uppercase">Itinerary Summaries</p>
                  <p className="text-2xl font-bold text-gray-800 mt-1">{result.summariesRenamed}</p>
                </div>
                <div className="p-3 bg-white rounded shadow-sm border border-gray-100">
                  <p className="text-xs text-gray-500 font-semibold uppercase">Metadata Updated</p>
                  <p className="text-2xl font-bold text-gray-800 mt-1">{result.metadataUpdated ? 'Yes' : 'No'}</p>
                </div>
                <div className="p-3 bg-white rounded shadow-sm border border-gray-100">
                  <p className="text-xs text-gray-500 font-semibold uppercase">User Cruises Logs</p>
                  <p className="text-2xl font-bold text-indigo-600 mt-1">{result.userCruisesRenamed}</p>
                </div>
              </div>
            ) : (
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
                <div className="p-3 bg-white rounded shadow-sm border border-gray-100">
                  <p className="text-xs text-gray-500 font-semibold uppercase">Itineraries Deleted</p>
                  <p className="text-2xl font-bold text-red-600 mt-1">{result.itinerariesDeleted}</p>
                </div>
                <div className="p-3 bg-white rounded shadow-sm border border-gray-100">
                  <p className="text-xs text-gray-500 font-semibold uppercase">Summaries Deleted</p>
                  <p className="text-2xl font-bold text-red-600 mt-1">{result.summariesDeleted}</p>
                </div>
                <div className="p-3 bg-white rounded shadow-sm border border-gray-100">
                  <p className="text-xs text-gray-500 font-semibold uppercase">Metadata Lists Cleaned</p>
                  <p className="text-2xl font-bold text-gray-800 mt-1">{result.metadataUpdated ? 'Yes' : 'No'}</p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default AdminShipItineraryManager;
